import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { Fragment, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Button } from 'common-src/components/base';
import { getRoleConfig } from 'common-src/features/auth';
import { deleteOrmItem } from 'common-src/features/rest';
import PatientDevice, {
  deletePatientDevice,
  getDeviceType,
  getPatientDevices,
} from 'common-src/models/PatientDevice';
import { singleModelSelector } from 'common-src/utils/selectorUtils';

import { ModalType } from 'src/components/base/ModalGroup';
import { openModal } from 'src/features/modals';
import useAlert from 'src/hooks/useAlert';
import useCustomSelector from 'src/hooks/useCustomSelector';
import BasePopup from 'src/popups/BasePopup';

import styles from './PatientDevicesPopup.module.scss';

const PatientDevicesPopup = ({ patientId, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();
  const { showAlert, AlertType } = useAlert();

  const devices = useCustomSelector((state) => getPatientDevices(state, patientId));
  const patient = useCustomSelector((state) => singleModelSelector(state, 'Patient', patientId));
  const canDeleteDevice = useCustomSelector(
    (state) => getRoleConfig(state)?.patientChart?.sections?.devicesAndOrders?.deleteBGMDevice,
  );

  const onDeleteHandler = (item) => {
    dispatch(
      openModal(ModalType.WARNING, {
        message: 'Are you sure you want to delete the device?',
        type: 'error',
        title: 'Delete Device',
        onSubmit: () => {
          setIsLoading(true);
          dispatch(
            deletePatientDevice(item.id, {
              successBlock: () => {
                deleteOrmItem(PatientDevice.modelName, item.id);
                showAlert(AlertType.Success, '', 'Device deleted!');
                setIsLoading(false);
              },
              errorBlock: (err) => {
                showAlert(AlertType.Error, '', err);
                setIsLoading(false);
              },
            }),
          );
        },
      }),
    );
  };

  return (
    <BasePopup id="devices" open onClose={onClose} title="Patient Devices">
      <div className="p-30">
        <div className={[styles.grid, 'm-b-10', 'font-w-600'].join(' ')}>
          <span>Device</span>
          <span>Last reading at</span>
          <span>Actions</span>
        </div>
        <div className={styles.grid}>
          {devices.map((device) => (
            <Fragment key={device.id}>
              <span>
                {getDeviceType(device.typeId)} #{device.id}
              </span>
              <span>
                {device.lastReadingAt
                  ? moment(device.lastReadingAt).tz(patient.timeZone).format('MM/DD/YYYY HH:mm A z')
                  : '-'}
              </span>
              <Button
                id="delete-device-btn"
                classNames={[styles.button]}
                text="Delete"
                onClick={() => onDeleteHandler(device)}
                backgroundColor="#F01F51B2"
                borderColor="transparent"
                isLoading={isLoading}
                disabled={!canDeleteDevice}
              />
            </Fragment>
          ))}
        </div>
      </div>
    </BasePopup>
  );
};

PatientDevicesPopup.propTypes = {
  onClose: PropTypes.func,
  patientId: PropTypes.number,
};

export default PatientDevicesPopup;
